import { useAuth } from '@/hooks';
import { useLoginModalStore } from '@/stores/login-modal-store';
import { useDepositModalStore } from '@/stores/deposit-modal-store';
import { useCurrency } from './useCurrency';
import { useCustomToast } from '@/hooks/use-suigar-toast';

/**
 * Custom hook to validate if a bet can be placed.
 * Returns a function that returns true if valid, otherwise handles modal and returns false.
 */
export function useBetValidation() {
	const { isLogged, availableBalance, profile } = useAuth();
	const { openLoginModal } = useLoginModalStore();
	const { openDepositModal } = useDepositModalStore();
	const { toBaseUnits } = useCurrency();
	const toast = useCustomToast();

	return (betAmount: number): boolean => {
		if (!isLogged) {
			openLoginModal();
			return false;
		}
		if (profile?.selfExclusion?.isActive) {
			const remainingDays = profile.selfExclusion.remainingDays;
			const endsAtRaw = profile.selfExclusion.endsAt;
			const endsAt = endsAtRaw ? new Date(endsAtRaw) : null;
			const endLabel =
				endsAt && Number.isNaN(endsAt.getTime()) === false
					? endsAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
					: null;
			const description =
				typeof remainingDays === 'number'
					? `You are self-excluded for ${remainingDays} more day(s).`
					: endLabel
						? `You are self-excluded until ${endLabel}.`
						: 'You are currently self-excluded from betting.';
			toast({
				title: 'Self-exclusion active',
				description,
				status: 'warning',
			});
			return false;
		}
		if (availableBalance == null) {
			return false;
		}
		if (availableBalance < toBaseUnits(betAmount)) {
			openDepositModal(true); // Set emptyBalance to true when balance is insufficient
			return false;
		}
		return true;
	};
}
